import { useUser } from "@/components/UserProvider";
import { ActionConfirmationModal } from "@/components/ui/ActionConfirmationModal/ActionConfirmationModal";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner/LoadingSpinner";
import { Button } from "@/components/ui/shadcn/Button/Button";
import {
  retrieveContactPhoneCallsInfiniteQueryOptions,
  retrieveContactQueryOptions,
  useDeleteContactPhoneCall,
} from "@/lib/data/react-query";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { ContactPhoneCall } from "@/lib/types";
import { formatContactsName } from "@/lib/utils/format";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Phone, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { CallForm } from "./CallForm";
import { PhoneCallCard } from "./PhoneCallCard";

export function ContactPhoneCallLogModal({
  open,
  setOpen,
  contactId,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  contactId: string;
}) {
  const { hasActionPermission } = useUser();
  const [formCall, setFormCall] = useState<ContactPhoneCall | "new">();
  const [deletingCall, setDeletingCall] = useState<ContactPhoneCall>();
  const deleteCall = useDeleteContactPhoneCall();
  const contactQuery = useQuery(retrieveContactQueryOptions(contactId));
  const callsQuery = useInfiniteQuery(
    retrieveContactPhoneCallsInfiniteQueryOptions({ contactId, limit: 25 }),
  );
  const calls = useMemo(
    () => callsQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [callsQuery.data],
  );
  const contact = contactQuery.data?.data;
  const contactName = formatContactsName(contact) ?? "Contact";
  const canCreate = hasActionPermission(
    ACTION_PERMISSION_KEYS.contact,
    ACTION_PERMISSION_VALUES.create,
  );
  const canEdit = hasActionPermission(
    ACTION_PERMISSION_KEYS.contact,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDelete = hasActionPermission(
    ACTION_PERMISSION_KEYS.contact,
    ACTION_PERMISSION_VALUES.delete,
  );

  useEffect(() => {
    if (!open) {
      setFormCall(undefined);
      setDeletingCall(undefined);
    }
  }, [open]);

  const title = formCall
    ? formCall === "new"
      ? `Log call with ${contactName}`
      : `Edit call with ${contactName}`
    : `Phone calls with ${contactName}`;

  return (
    <>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        title={title}
        description="Review and record phone calls for this contact."
        size="2xl"
        bodyContained
        bodyClassName="min-h-[520px]"
      >
        {formCall ? (
          <div className="h-full overflow-y-auto px-6 py-5">
            <CallForm
              call={formCall === "new" ? undefined : formCall}
              contactId={contactId}
              contactPhoneNumbers={
                contact?.phones.map((phone) => ({
                  id: phone.id,
                  label: phone.label ?? "",
                  number: phone.number,
                })) ?? []
              }
              onCancel={() => {
                setFormCall(undefined);
              }}
              onSaved={() => {
                setFormCall(undefined);
              }}
            />
          </div>
        ) : (
          <div className="flex h-full min-h-0 flex-col">
            <div className="border-border flex shrink-0 items-center justify-between gap-4 border-b px-6 py-4">
              <div>
                <p className="font-medium">Call history</p>
                <p className="text-muted-foreground text-sm">
                  {calls.length
                    ? `${calls.length} call${calls.length === 1 ? "" : "s"} loaded`
                    : "Completed calls appear here"}
                </p>
              </div>
              {canCreate ? (
                <Button
                  type="button"
                  onClick={() => {
                    setFormCall("new");
                  }}
                >
                  <Plus /> Log call
                </Button>
              ) : null}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              {callsQuery.isLoading || contactQuery.isLoading ? (
                <div className="grid h-full place-items-center">
                  <LoadingSpinner className="size-7" />
                </div>
              ) : callsQuery.isError || contactQuery.isError ? (
                <div className="grid h-full place-items-center text-center">
                  <div>
                    <p className="font-medium">Couldn’t load phone calls</p>
                    <p className="text-muted-foreground mt-1 text-sm">
                      Try loading the call history again.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-4"
                      onClick={() => void callsQuery.refetch()}
                    >
                      Try again
                    </Button>
                  </div>
                </div>
              ) : calls.length === 0 ? (
                <div className="grid h-full place-items-center text-center">
                  <div className="max-w-sm">
                    <span className="bg-muted mx-auto grid size-12 place-items-center rounded-full">
                      <Phone className="text-muted-foreground size-5" />
                    </span>
                    <p className="mt-4 font-medium">No phone calls yet</p>
                    <p className="text-muted-foreground mt-1 text-sm">
                      Record a completed call to keep this contact’s
                      communication history together.
                    </p>
                    {canCreate ? (
                      <Button
                        type="button"
                        className="mt-4"
                        onClick={() => {
                          setFormCall("new");
                        }}
                      >
                        <Plus /> Log call
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {calls.map((call) => (
                    <PhoneCallCard
                      key={call.id}
                      call={call}
                      contactId={contactId}
                      contactName={contactName}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      onEdit={() => {
                        setFormCall(call);
                      }}
                      onDelete={() => {
                        setDeletingCall(call);
                      }}
                    />
                  ))}
                  {callsQuery.hasNextPage ? (
                    <div className="flex justify-center pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={callsQuery.isFetchingNextPage}
                        onClick={() => void callsQuery.fetchNextPage()}
                      >
                        {callsQuery.isFetchingNextPage ? (
                          <LoadingSpinner />
                        ) : null}
                        Load older calls
                      </Button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )}
      </AppModal>
      <ActionConfirmationModal
        open={Boolean(deletingCall)}
        setOpen={(nextOpen) => {
          if (!nextOpen) setDeletingCall(undefined);
        }}
        title="Delete phone call"
        description="Delete this manually recorded phone call? This action cannot be undone."
        buttonText="Delete call"
        isActionPending={deleteCall.isPending}
        afterActionFn={async () => {
          if (!deletingCall) return;
          try {
            await deleteCall.mutateAsync({ id: deletingCall.id });
            setDeletingCall(undefined);
          } catch {
            // The mutation hook displays the API error and leaves confirmation open.
          }
        }}
      />
    </>
  );
}
